import React, { forwardRef } from 'react'
import './PrintPreviewStyle.css'
import type {
  ClinicInfo,
  PatientInfo,
  ReportImage,
  ReportConfig,
} from '../reportTypes'

interface PrintPreviewProps {
  clinicInfo: ClinicInfo
  patientInfo: PatientInfo
  selectedImages: ReportImage[]
  reportConfig: ReportConfig
  fullWidth?: boolean
}

const PrintPreview = forwardRef<HTMLDivElement, PrintPreviewProps>(
  (
    { clinicInfo, patientInfo, selectedImages, reportConfig, fullWidth },
    ref,
  ) => {
    const issuedDate = new Date().toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })

    const showFindings =
      reportConfig.reportType === 'Full' ||
      Object.values(reportConfig.categories).some(Boolean)

    const showRecommendations = reportConfig.categories['Recommendations']

    return (
      <div
        ref={ref}
        className={`pp-report ${fullWidth ? 'pp-report-fullwidth' : ''}`}
      >
        <div className="pp-header">
          <div className="pp-clinic-row">
            <div className="pp-clinic-logo">
              {clinicInfo.logoUrl ? (
                <img
                  src={clinicInfo.logoUrl}
                  alt={clinicInfo.name}
                  crossOrigin="anonymous"
                />
              ) : (
                <div className="pp-logo-placeholder">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="2"
                  >
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                </div>
              )}
            </div>
            <div className="pp-clinic-info">
              <h6 className="pp-clinic-title">Radiography report</h6>
              <p className="pp-clinic-name">{clinicInfo.name}</p>
              <p className="pp-clinic-address">{clinicInfo.address}</p>
              <p className="pp-clinic-address">{clinicInfo.city}</p>
            </div>
            <div className="pp-issued-date">
              <span>Issued on {issuedDate}</span>
            </div>
          </div>
        </div>

        <div className="pp-divider" />

        <div className="pp-patient-section">
          <div className="pp-patient-row">
            <span>
              Patient name: <strong>{patientInfo.name}</strong>
            </span>
            <span>
              Gender: <strong>{patientInfo.gender}</strong>
            </span>
            <span>
              Age: <strong>{patientInfo.age}</strong>
            </span>
          </div>
          <div className="pp-patient-row">
            <span>
              Scan Date: <strong>{patientInfo.scanDate}</strong>
            </span>
            <span>
              Scan type: <strong>{patientInfo.scanType}</strong>
            </span>
          </div>
        </div>

        <div className="pp-images-section">
          {selectedImages.length > 0 ? (
            selectedImages.map((img) => (
              <div key={img.id} className="pp-image-wrapper">
                {img.imageUrl ? (
                  <img
                    src={img.imageUrl}
                    alt={img.fileName}
                    className="pp-xray-image"
                    crossOrigin="anonymous"
                  />
                ) : (
                  <div className="pp-image-placeholder">
                    <span>X-Ray Image</span>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="pp-image-placeholder">
              <span>No images selected</span>
            </div>
          )}
        </div>

        {showFindings && (
          <div className="pp-findings-section">
            <h6 className="pp-section-title">Findings</h6>
            <p className="pp-section-text">
              The panoramic dental X-ray shows well-aligned upper and lower
              teeth with no significant abnormalities. The bone structure and
              temporomandibular joints (TMJ) appear normal, and there are no
              signs of significant bone loss or caries. The wisdom teeth are
              partly erupted with potential impaction, particularly the lower
              third molars, which may require monitoring or future extraction.
              Overall, the patient&apos;s dental condition is healthy, with
              routine dental care recommended.
            </p>
          </div>
        )}

        {showRecommendations && (
          <div className="pp-recommendations-section">
            <h6 className="pp-section-title">Recommendations</h6>
            <ul className="pp-recommendations-list">
              <li>
                <strong>Wisdom Teeth:</strong> Due to the potential impaction of
                the lower third molars, it is recommended to monitor these teeth
                for any signs of discomfort or infection.
              </li>
              <li>
                <strong>Routine Follow-up:</strong> Regular dental check-ups and
                cleanings are advised to maintain dental health.
              </li>
              <li>
                <strong>Further Imaging:</strong> If any symptoms develop or if
                more detailed assessment is needed, consider additional imaging
                such as periapical X-rays or CBCT.
              </li>
            </ul>
          </div>
        )}

        <div className="pp-signature-section">
          <div className="pp-signature-block">
            <p>Radiologist/Dentist Signature: ___________________</p>
            <p>Report Date: {patientInfo.scanDate}</p>
          </div>
        </div>
      </div>
    )
  },
)

PrintPreview.displayName = 'PrintPreview'

export default PrintPreview
